import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { PRODUCT_NAMES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", company: "", phone: "", product: "", quantity: "", message: "" };

const inputCls =
  "h-12 rounded-xl border-[var(--border)] bg-white/70 focus-visible:ring-[var(--green)] text-[15px]";

export default function InquiryForm({ defaultProduct = "", onSuccess, compact = false }) {
  const [form, setForm] = useState({ ...EMPTY, product: defaultProduct });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    setForm((f) => ({ ...f, product: defaultProduct || f.product }));
  }, [defaultProduct]);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.phone.trim()) next.phone = "Please enter your phone number.";
    else if (!/^\+?[0-9\s-]{7,15}$/.test(form.phone.trim())) next.phone = "Enter a valid phone number.";
    if (!form.product.trim()) next.product = "Please select a product.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields and try again.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/inquiries`, form, {
        headers: { "Content-Type": "application/json" },
        timeout: 15000,
      });
      if (res?.data?.id) {
        onSuccess?.();
        navigate("/request-received");
      } else {
        throw new Error("Unexpected response from server.");
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (err?.code === "ECONNABORTED" ? "Request timed out." : null) ||
        err?.message ||
        "Something went wrong.";
      toast.error(`Could not send your enquiry. ${detail}. Please call us at ${process.env.REACT_APP_PHONE || "7676011496"}.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
      <div className="space-y-1.5">
        <Label className="text-[var(--ink-soft)]">Name *</Label>
        <Input data-testid="inquiry-name" className={inputCls} placeholder="Your full name" value={form.name} onChange={set("name")} aria-invalid={!!errors.name} />
        {errors.name && <p data-testid="error-name" className="text-xs text-red-600">{errors.name}</p>}
      </div>

      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div className="space-y-1.5">
          <Label className="text-[var(--ink-soft)]">Phone Number *</Label>
          <Input data-testid="inquiry-phone" className={inputCls} placeholder="Mobile number" value={form.phone} onChange={set("phone")} aria-invalid={!!errors.phone} />
          {errors.phone && <p data-testid="error-phone" className="text-xs text-red-600">{errors.phone}</p>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-[var(--ink-soft)]">Product Required *</Label>
          <Select value={form.product} onValueChange={(v) => { setForm((f) => ({ ...f, product: v })); if (errors.product) setErrors((p) => ({ ...p, product: undefined })); }}>
            <SelectTrigger data-testid="inquiry-product" className={`${inputCls} data-[placeholder]:text-muted-foreground`}>
              <SelectValue placeholder="Select a product" />
            </SelectTrigger>
            <SelectContent>
              {PRODUCT_NAMES.map((p) => (
                <SelectItem key={p} value={p} data-testid={`product-option-${p}`}>
                  {p}
                </SelectItem>
              ))}
              <SelectItem value="Multiple Products">Multiple Products</SelectItem>
            </SelectContent>
          </Select>
          {errors.product && <p data-testid="error-product" className="text-xs text-red-600">{errors.product}</p>}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-[var(--ink-soft)]">Quantity</Label>
        <Input data-testid="inquiry-quantity" className={inputCls} placeholder="e.g. 500 kg / 50 bags" value={form.quantity} onChange={set("quantity")} />
      </div>

      <div className="space-y-1.5">
        <Label className="text-[var(--ink-soft)]">Message</Label>
        <Textarea data-testid="inquiry-message" className="min-h-[110px] rounded-xl border-[var(--border)] bg-white/70 focus-visible:ring-[var(--green)] text-[15px]" placeholder="Tell us about your requirement, delivery location, etc." value={form.message} onChange={set("message")} />
      </div>

      <Button
        type="submit"
        data-testid="inquiry-submit"
        disabled={submitting}
        className="group h-13 w-full rounded-full bg-[var(--green)] py-6 text-base font-semibold text-white transition-all duration-300 hover:bg-[var(--green-deep)] hover:shadow-lg hover:shadow-green-900/20"
      >
        {submitting ? <Loader2 className="animate-spin" /> : <Send className="transition-transform group-hover:translate-x-1" />}
        {submitting ? "Sending..." : "Get Wholesale Quote"}
      </Button>
      <p className="text-center text-xs text-[var(--ink-soft)]">We respond within business hours. Or call directly at {" "}
        <a href="tel:7676011496" className="font-semibold text-[var(--green)]">7676011496</a>.
      </p>
    </form>
  );
}
